import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useCallback, useMemo, useState } from 'react'
import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import ECharts from '~/components/echarts'
import Select from '~/components/select'
import {
  awsEBSBandwidthReadMetricsResourceIdChartPath,
  awsEBSBandwidthWriteMetricsResourceIdChartPath,
  awsEBSIdleTimeMetricsResourceIdChartPath,
  awsEBSIopsReadMetricsResourceIdChartPath,
  awsEBSIopsWriteMetricsResourceIdChartPath,
  awsEBSQueueLengthMetricsResourceIdChartPath,
  awsEBSReadBlockMetricsResourceIdChartPath,
  awsEBSWriteBlockMetricsResourceIdChartPath,
} from '~/routes'

export default function AWSEBSMetricsCharts({ organizationId: orgId, resourceId: reId }) {
  const [params, setParams] = useState({ metrics_time_frame: '1w' })
  const metricsTimeFrames = useMemo(
    () => [
      { label: t`1 Week`, value: '1w', default: true },
      { label: t`1 Month`, value: '1m' },
      { label: t`3 Months`, value: '3m' },
    ],
    [],
  )

  const onChange = useCallback((name, values) => {
    // update params when a filter is selected
    setParams((prevParams) => ({ ...prevParams, [name]: values }))
  }, [])

  const awsEBSIopsReadMetricsChartUrl = useMemo(
    () => awsEBSIopsReadMetricsResourceIdChartPath(orgId, reId),
    [orgId, reId],
  )
  const awsEBSIopsWriteMetricsChartUrl = useMemo(
    () => awsEBSIopsWriteMetricsResourceIdChartPath(orgId, reId),
    [orgId, reId],
  )
  const awsEBSBandwidthReadMetricsResourceIdChartUrl = useMemo(
    () => awsEBSBandwidthReadMetricsResourceIdChartPath(orgId, reId),
    [orgId, reId],
  )
  const awsEBSBandwidthWriteMetricsResourceIdChartUrl = useMemo(
    () => awsEBSBandwidthWriteMetricsResourceIdChartPath(orgId, reId),
    [orgId, reId],
  )
  const awsEBSIdleTimeMetricsResourceIdChartUrl = useMemo(
    () => awsEBSIdleTimeMetricsResourceIdChartPath(orgId, reId),
    [orgId, reId],
  )
  const awsEBSQueueLengthMetricsResourceIdChartUrl = useMemo(
    () => awsEBSQueueLengthMetricsResourceIdChartPath(orgId, reId),
    [orgId, reId],
  )
  const awsEBSReadBlockMetricsResourceIdChartUrl = useMemo(
    () => awsEBSReadBlockMetricsResourceIdChartPath(orgId, reId),
    [orgId, reId],
  )
  const awsEBSWriteBlockMetricsResourceIdChartUrl = useMemo(
    () => awsEBSWriteBlockMetricsResourceIdChartPath(orgId, reId),
    [orgId, reId],
  )

  return (
    <div className='card'>
      <h3 className='card-header'>
        <Trans>Cloudwatch Information</Trans>
      </h3>
      <div className='card-body'>
        <div className='row mb-3 gx-2 gy-2 align-items-center'>
          <div className='col-sm-4'>
            <Select
              label={t`Time Frame`}
              name='metrics_time_frame'
              onChange={onChange}
              options={metricsTimeFrames}
              selected={params.metrics_time_frame}
            />
          </div>
        </div>
        <Tabs id='uncontrolled-metrics-tabs' className='mb-3'>
          <Tab eventKey='ebs-iops-tab' title={t`IOPS`}>
            <div className='row g-0'>
              <div className='col-sm-12'>
                <ECharts
                  title={t`Read Operations`}
                  chartInfoTitle='aws_ebs_read_operations'
                  url={awsEBSIopsReadMetricsChartUrl}
                  params={params}
                />
              </div>
              <div className='col-sm-12'>
                <ECharts
                  title={t`Write Operations`}
                  chartInfoTitle='aws_ebs_write_operations'
                  url={awsEBSIopsWriteMetricsChartUrl}
                  params={params}
                />
              </div>
            </div>
          </Tab>
          <Tab eventKey='ebs-bandwidth-tab' title={t`Bandwidth`} mountOnEnter>
            <div className='row g-0'>
              <div className='col-sm-12'>
                <ECharts
                  title={t`Bandwidth Read`}
                  chartInfoTitle='aws_ebs_bandwidth_read'
                  url={awsEBSBandwidthReadMetricsResourceIdChartUrl}
                  params={params}
                />
              </div>
              <div className='col-sm-12'>
                <ECharts
                  title={t`Bandwidth Write`}
                  chartInfoTitle='aws_ebs_bandwidth_write'
                  url={awsEBSBandwidthWriteMetricsResourceIdChartUrl}
                  params={params}
                />
              </div>
            </div>
          </Tab>
          <Tab eventKey='ebs-queue-tab' title={t`Queue / Idle / Block`} mountOnEnter>
            <div className='row g-0'>
              <div className='col-sm-12'>
                <ECharts
                  title={t`Idle Time`}
                  chartInfoTitle='aws_ebs_idle_time'
                  url={awsEBSIdleTimeMetricsResourceIdChartUrl}
                  params={params}
                />
              </div>
              <div className='col-sm-12'>
                <ECharts
                  title={t`Queue Length`}
                  chartInfoTitle='aws_ebs_queue_length'
                  url={awsEBSQueueLengthMetricsResourceIdChartUrl}
                  params={params}
                />
              </div>
              <div className='col-sm-12'>
                <ECharts
                  title={t`Read Block`}
                  chartInfoTitle='aws_ebs_read_block'
                  url={awsEBSReadBlockMetricsResourceIdChartUrl}
                  params={params}
                />
              </div>
              <div className='col-sm-12'>
                <ECharts
                  title={t`Write Block`}
                  chartInfoTitle='aws_ebs_write_block'
                  url={awsEBSWriteBlockMetricsResourceIdChartUrl}
                  params={params}
                />
              </div>
            </div>
          </Tab>
        </Tabs>
      </div>
    </div>
  )
}
